import { Loader } from "@talkouttech/portal-shared";
import * as React from "react";
import { Tenant } from "../domain/company";
import { client } from "../utils/api-client";
import { useLocalStorage } from "../utils/hooks";
import { useAuth } from "./auth-context";
export type TenantContextType = {
  setActiveTenant: (tenantId: string) => void;
  tenants: Tenant[];
  activeTenant: string;
};

const TenantContext: React.Context<TenantContextType> = React.createContext<TenantContextType>({
  setActiveTenant: () => {},
  tenants: [],
  activeTenant: ""
});

TenantContext.displayName = "TenantContext";

function getInitialTenant(): string | null {
  try {
    const segments = window.location.pathname.split("/").filter(Boolean);
    const tenantFromUrl = segments[0];
    const stored = window.localStorage.getItem("portalActiveTenant");

    if (tenantFromUrl && segments.length >= 2) {
      const parsed = JSON.stringify(tenantFromUrl);
      if (stored !== parsed) {
        window.localStorage.setItem("portalActiveTenant", parsed);
      }
      return tenantFromUrl;
    }

    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function TenantProvider(props: any): JSX.Element {
  const { identity, isAuthenticated, isLoading: isAuthLoading } = useAuth();

  const [tenants, setTenants] = React.useState<Tenant[] | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [activeTenant, setTenant] = useLocalStorage<string | null>("portalActiveTenant", getInitialTenant());

  const setActiveTenant = (tenantId: string) => setTenant(tenantId);

  React.useEffect(() => {
    setIsLoading(true);

    if (!isAuthenticated && !isAuthLoading) {
      setTenant(null);
    }

    if (isAuthenticated && !!activeTenant && identity) {
      if (!identity?.tenantInfo[activeTenant] && !identity.permissions.includes("tenant:admin-read")) {
        setTenant(null);
        window.location.href = "/home";
      }
    }

    if (identity && tenants === null) {
      client<Tenant[]>("tenants", { token: identity.token }).then(fetchedTenants => {
        if (!identity) return;

        setTenants(fetchedTenants);
        if (!activeTenant) {
          setTenant(fetchedTenants[0]?.id);
        }
        setIsLoading(false);
      });
    } else {
      setIsLoading(false);
    }
  }, [activeTenant, setTenant, identity, isAuthenticated, tenants, isAuthLoading]);

  if (isLoading) return <Loader />;

  const value = { tenants, activeTenant, setActiveTenant };
  return <TenantContext.Provider value={value} {...props} />;
}

function useTenantContext() {
  const context = React.useContext(TenantContext);

  if (context === undefined) {
    throw new Error(`useTenantContext must be used within a TenantProvider`);
  }
  return context;
}

export { TenantProvider, useTenantContext };
